import appConfig from "../application/appConfig";
import { Base } from "../framework/common/base";
import { Session } from "../framework/session/session";
import { SessionState, ISessionState } from "../framework/session/sessionState";
import { authorizationHeaderRefreshTokenValue, getJsonHeaders, handleApiError, handleError, handleResponse } from "./baseService";
import { store } from "../framework/redux/store";
import { clearSession, setSession } from "../framework/redux/slices/sessionSlice";
import { addAlert } from "../framework/redux/slices/appSlice";
import { AlertSeverity } from "../models/common/enums";
import i18next, { t } from "i18next";

export const login = (username: string, password: string): Promise<ISessionState> => {
    const requestOptions = {
        headers: {
            "Accept": "application/json",
            "Content-Type": "application/json;charset=utf-8",
            "Authorization": "Basic " + Base.b64EncodeUnicode(username) + ":" + Base.b64EncodeUnicode(password)
        }
    };
    return fetch(appConfig.serverUrl + "api/token/mobile", requestOptions)
        .then(handleResponse)
        .catch(handleApiError)
        .then(data => {
            if (!data || !data.accessToken) return null;
            const sessionState = new SessionState(data);
            Session.setSessionState(sessionState).catch(e => console.log(e));
            i18next.changeLanguage(sessionState.defaultCulture);
            return { ...sessionState };
        });
};

export const logout = async() : Promise<void> => {
    try {
        const sessionState = await Session.getSessionState();
        if (sessionState) {
            await logoutCall().catch(e => console.log(e));
        }
        return await new Promise<void>((resolve) => { resolve(); });
    } finally {
        await Session.setSessionState(null);
    }
};

const logoutCall = (): Promise<any> => {
    return getJsonHeaders().then(i => {
        const requestOptions = {
            headers: i
        };
        return fetch(appConfig.serverUrl + "api/token/logout", requestOptions)
            .then(handleResponse, handleError);
    });
};

export const refreshLogin = (): Promise<ISessionState> => {
    return authorizationHeaderRefreshTokenValue().then(i => {
        const requestOptions = {
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json;charset=utf-8",
                "Authorization": i,
                "x-login-origin": "mobile"
            }
        };
        return fetch(appConfig.serverUrl + "api/token/refresh", requestOptions)
            .then(handleResponse, handleError)
            .then((data: ISessionState) => {
                if (!data || !data.accessToken) {
                    return null;
                }
                const sessionState = new SessionState(data);
                Session.setSessionState(sessionState).catch(e => console.log(e));
                store.dispatch(setSession(data));
                return { ...sessionState };
            });
    });
};

export const refreshAuthentication = async(force = false): Promise<void> => {
    const handleRefreshError = e => {
        if (e.code === "401") {
            store.dispatch(clearSession());
            store.dispatch(
                addAlert({
                    message: t("login.sessionExpired"),
                    severity: AlertSeverity.Info,
                    debounce: true,
                })
            );
        } else {
            handleApiError(e);
        }
    };

    if (force) {
        await refreshLogin().catch(handleRefreshError);
        return;
    } else {
        const refresh = await Session.tokenNeedsRefresh();
        if (refresh) {
            await refreshLogin().catch(handleRefreshError);
            return;
        } else {
            return new Promise((resolve) => {
                resolve();
            });
        }
    }
};

interface IRequestOptions {
    method: string;
    headers: any;
    body?: string;
    signal?: AbortSignal;
}

/**
 * Make api call using what ever class for calling.
 *
 * @param url The url to call
 * @param signal signal to abort the operation.
 * @param method The method.
 * @param data The data - any kind class that back end support
 */
export const makeApiCallWithAny = <T>(url: string, signal?: AbortSignal, method = "GET", data: any = null): Promise<T> => {
    return refreshAuthentication().then(() => {
        return getJsonHeaders(data).then(i => {
            const requestOptions = {
                method,
                headers: i,
                signal,
            } as IRequestOptions;

            if (data) {
                requestOptions.body = JSON.stringify(data);
            }

            return fetch(appConfig.serverUrl + url, requestOptions)
                .then(handleResponse, handleError)
                .then((data) => <T>data);
        });
    });
};
